import axios, { type InternalAxiosRequestConfig } from 'axios'
import i18n from '@/i18n/config'
import { refreshAccessToken } from '@/api/refreshToken'
import { settleRefreshOutcome } from '@/api/authSession'
import { clientHeaders } from '@/api/clientInfo'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { API_ENDPOINTS, STORAGE_KEYS } from '@/utils/constants'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4545/api'

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

const AUTH_REQUEST_PATHS = [
  API_ENDPOINTS.AUTH.LOGIN,
  API_ENDPOINTS.AUTH.REGISTER,
  API_ENDPOINTS.AUTH.REFRESH,
  API_ENDPOINTS.AUTH.CLIENT_EVENTS,
]

function bearerToken(config: InternalAxiosRequestConfig): string | null {
  const header = config.headers?.Authorization
  if (typeof header !== 'string') {
    return null
  }
  const match = /^Bearer\s+(.+)$/i.exec(header)

  return match ? match[1] : null
}

/** Заголовки клиента — только для запросов к API, не к сторонним адресам */
function isApiRequest(config: InternalAxiosRequestConfig): boolean {
  const url = config.url ?? ''

  return !/^[a-z][a-z\d+.-]*:\/\//i.test(url) || url.startsWith(API_URL)
}

// Interceptor для добавления JWT токена и локали
apiClient.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    // Явно переданный заголовок не перетираем: так запрос уходит со свежим
    // токеном, который ещё не лёг в localStorage (см. mergeSubscriptionParameters).
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const currentLanguage = i18n.language || 'ru'
    const normalizedLanguage = currentLanguage.split('-')[0].toLowerCase()
    const apiLanguage = ['ru', 'en'].includes(normalizedLanguage) ? normalizedLanguage : 'ru'
    config.headers['Accept-Language'] = apiLanguage

    if (isApiRequest(config)) {
      for (const [name, value] of Object.entries(clientHeaders())) {
        config.headers[name] = value
      }
    }

    return config
  },
  (error) => Promise.reject(error),
)

// Interceptor для обработки ошибок и silent refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const requestUrl = originalRequest.url ?? ''
      const isAuthRequest = AUTH_REQUEST_PATHS.some((path) => requestUrl.includes(path))

      if (!isAuthRequest) {
        originalRequest._retry = true
        const outcome = await refreshAccessToken(bearerToken(originalRequest))

        if (outcome.status === 'ok') {
          originalRequest.headers.Authorization = `Bearer ${outcome.token}`
          return apiClient(originalRequest)
        }
        // failed — причина выхода и вход, ended — только уборка. transient
        // (сеть, 5xx, 429): сессия остаётся, запрос завершается ошибкой,
        // следующий запрос попробует обменять токен снова
        settleRefreshOutcome(outcome)
      }
    }

    if (error.response?.status === 403 && error.response?.data?.error === 'subscription_required') {
      const message = error.response?.data?.message || i18n.t('subscription.teacherSuspended')
      useSubscriptionStore.getState().setSubscriptionRequiredMessage(message)
    }

    return Promise.reject(error)
  },
)
